import {Color, Group, MathUtils, type Object3D, type WebGLRenderer} from 'three'

import {
  EFFECT_RING_SECONDS,
  EFFECT_SHADOW_FADE_SECONDS,
  EFFECT_SPARK_DELAY_SECONDS,
  EFFECT_TOTAL_SECONDS,
  RING_MAX_RADIUS_M,
  RING_MIN_RADIUS_M,
  RING_RADIUS_SCALE,
  SHADOW_OPACITY,
  SPARK_SPREAD_RATIO,
  SPARK_WHITEN,
} from '../config'
import type {PlacementConfig} from '../content/manifest.types'
import type {ShadowCatcher} from '../scene/lighting'
import {easeOutCubic, ramp01} from '../util/easing'
import {createShockwaveRing, type ShockwaveRing} from './shockwave-ring'
import {createSparkBurst, type SparkBurst} from './spark-burst'

const WHITE = new Color(1, 1, 1)

/**
 * The landing impact: a shockwave ring, a burst of sparks, and the contact
 * shadow fading in under them.
 *
 * This file owns the beat sheet. The ring and the sparks hold no clock of their
 * own - they are told what state to be in and draw it - which is the same split
 * as `animation.ts` owning the mixer while `app-module.ts` owns the delta.
 *
 * It touches the model not at all. No material swap, no clipping plane, nothing
 * that a differently-authored GLB could break. That is the whole design
 * constraint: `campaign.json` plus a new .glb has to keep working.
 *
 * OWNERSHIP: once this exists, it is the ONLY thing allowed to write the
 * placed shadow catcher's `material.opacity`. With `effect: 'none'` it never
 * writes it at all, so an opted-out campaign renders bit-for-bit as before.
 *
 * PARENTING: `root` belongs under `PlacementController.root`, and must be added
 * AFTER `setContent()` - that calls `root.clear()` and would take this with it.
 * Living under the placement root is what keeps the light welded to the model's
 * feet through re-placement and, later, through pinch-scale. The usual
 * objection - "an effect in flight would teleport" - cannot happen here, because
 * every placement hard-restarts the timeline (see `play`).
 */

export type EffectWarning =
  | {readonly kind: 'radiusClamped'; readonly requested: number; readonly applied: number}
  | {readonly kind: 'noShadowCatcher'}

export interface EntranceEffect {
  /** Add this under `PlacementController.root`, after `setContent()`. */
  readonly root: Object3D
  play(): void
  update(deltaSeconds: number): void
  readonly isPlaying: boolean
  readonly elapsedSeconds: number
  readonly warnings: readonly EffectWarning[]
  dispose(): void
}

export interface EntranceEffectDeps {
  readonly renderer: WebGLRenderer
  readonly config: PlacementConfig
  /**
   * Already resolved, in the working colour space.
   *
   * Passed in rather than read from `config` here, because deriving it from the
   * model means reading the model - and not reading the model is this file's
   * whole contract. See src/model/model-color.ts.
   */
  readonly accentColor: Color
  /** `NormalizedModel.groundRadius`. Sizes the whole effect. */
  readonly groundRadius: number
  /** `null` skips the shadow fade and records a warning. */
  readonly shadowCatcher: ShadowCatcher | null
}

export const describeEffectWarning = (warning: EffectWarning): string => {
  switch (warning.kind) {
    case 'radiusClamped':
      return `ring radius ${warning.requested.toFixed(3)} m clamped to ${warning.applied.toFixed(3)} m`
    case 'noShadowCatcher':
      return 'no shadow catcher supplied; the contact shadow will not fade in'
  }
}

export const createEntranceEffect = ({
  renderer,
  config,
  accentColor,
  groundRadius,
  shadowCatcher,
}: EntranceEffectDeps): EntranceEffect => {
  const warnings: EffectWarning[] = []

  // Mirrors createAnimationController's no-clip early return: an opted-out
  // campaign gets a real object with the same shape, so no call site anywhere
  // has to know the difference.
  if (config.effect === 'none') {
    const empty = new Group()
    empty.name = 'entrance-effect-disabled'
    return {
      root: empty,
      play: () => {},
      update: () => {},
      isPlaying: false,
      elapsedSeconds: 0,
      warnings,
      dispose: () => {},
    }
  }

  const requestedRadius = groundRadius * RING_RADIUS_SCALE
  const maxRadius = MathUtils.clamp(requestedRadius, RING_MIN_RADIUS_M, RING_MAX_RADIUS_M)
  if (maxRadius !== requestedRadius) {
    warnings.push({kind: 'radiusClamped', requested: requestedRadius, applied: maxRadius})
  }
  if (shadowCatcher === null) warnings.push({kind: 'noShadowCatcher'})

  // Cloned so nothing downstream can mutate the caller's colour through the
  // uniform - both primitives hold the instance by reference.
  const color = accentColor.clone()
  // The ring carries the hue; the sparks read as light. Lerping toward white in
  // the LINEAR working space is the physically right move here - it is adding
  // white light, which is exactly what additive blending then does to the
  // camera feed. Without it, a dark hue drags the sparks down with it and they
  // stop registering as sparkle.
  const sparkColor = color.clone().lerp(WHITE, SPARK_WHITEN)

  const ring: ShockwaveRing = createShockwaveRing({maxRadiusMeters: maxRadius, color})
  const sparks: SparkBurst = createSparkBurst({
    renderer,
    spreadMeters: maxRadius * SPARK_SPREAD_RATIO,
    color: sparkColor,
  })

  const group = new Group()
  group.name = 'entrance-effect'
  group.visible = false
  group.add(ring.object, sparks.object)

  let elapsed = 0
  let playing = false

  const apply = (): void => {
    ring.setProgress(elapsed / EFFECT_RING_SECONDS)
    sparks.setElapsed(elapsed - EFFECT_SPARK_DELAY_SECONDS)
    if (shadowCatcher !== null) {
      shadowCatcher.material.opacity =
        SHADOW_OPACITY * easeOutCubic(ramp01(elapsed, 0, EFFECT_SHADOW_FADE_SECONDS))
    }
  }

  // Safe to write here: the placement root is still hidden at construction time,
  // so nothing has been drawn with the full-strength shadow yet.
  if (shadowCatcher !== null) shadowCatcher.material.opacity = 0

  return {
    root: group,
    get isPlaying() {
      return playing
    },
    get elapsedSeconds() {
      return elapsed
    },
    warnings,

    play: () => {
      // Always a hard restart. A tap during playback is the same gesture as the
      // first one, and `animation.playIntro()` restarts its clip on that very
      // same event - the two must not disagree about what a tap means, or the
      // light and the character drift out of phase.
      elapsed = 0
      playing = true
      group.visible = true
      sparks.reseed()
      // Apply frame 0 now, for the same reason playIntro calls mixer.update(0):
      // a pointerdown can land after this frame's onUpdate has already run, and
      // without this the first rendered frame would show the previous run's
      // final state at the new location.
      apply()
    },

    update: (deltaSeconds) => {
      if (!playing) return
      elapsed += deltaSeconds
      apply()
      if (elapsed >= EFFECT_TOTAL_SECONDS) {
        playing = false
        // Idle costs nothing: no draw calls until the next placement.
        group.visible = false
        // Land exactly on the target rather than wherever the last delta fell.
        if (shadowCatcher !== null) shadowCatcher.material.opacity = SHADOW_OPACITY
      }
    },

    dispose: () => {
      ring.dispose()
      sparks.dispose()
      group.removeFromParent()
      // Hand the shadow back at full strength. Disposing mid-flight must not
      // leave a permanently invisible contact shadow behind.
      if (shadowCatcher !== null) shadowCatcher.material.opacity = SHADOW_OPACITY
    },
  }
}
